// The PT9 single door (docs/claude/glazings.md, uploads/PT9.dxf): a steam room's
// outer door with no glass beside it — an aluminium frame of one profile round
// three sides, mitred at the head, and the PT10 door leaf in it (its glass,
// hinges and handle, glazing.ts `leafSolids`). Laid out in the glazing frame (x
// from the frame's outer left edge seen from outside, y up, z from the glass
// mid-plane, + outside), hinges on the right, mirrored for hinges on the left.
// Pure and DOM-free (test/glazing.test.mjs).

import {
  type GlazingInput,
  type GlazingLayoutV,
  type GlazingPlan,
  type GlazingSolid,
  type GlazingWarning,
  leafSolids,
  loopsOf,
  mirrorSolid,
  orient,
  type P3,
} from './glazing';
import {
  PT9_CLIP,
  PT9_HINGE_ARM,
  PT9_HINGE_BRACKET,
  PT9_LIP,
  PT9_PROFILE,
} from './singleDoorData';

/** The air gap between the frame and the wall, each side and over the head (mm). */
export const SINGLE_GAP = 5;
/** The largest wall opening the spec allows (mm), wall to wall and floor to head. */
export const SINGLE_W_MAX = 1010;
export const SINGLE_H_MAX = 2500;
/** The drawing's door: its wall opening (frame 969 × 2059, glass passage 915). */
export const SINGLE_DEFAULT = { width: 979, height: 2064 };
/** How deep the frame is: its outer face (z), set flush with the wall's outer face. */
export const SINGLE_Z_OUT = 32.7;
/** The profile's width in the plan, jamb and head alike (mm). */
export const SINGLE_BAND = 38;

// The smallest frame the leaf, its handle and its two hinges still fit in: past
// the spec the door still builds, as near as it can.
const GEO_MIN_W = 400, GEO_MIN_H = 1200;

/** A single door worked out, as a glazing's plan (no corner, never panes). */
export function planSingleDoor(input: GlazingInput): GlazingPlan {
  const warnings: GlazingWarning[] = [];
  if (input.W > SINGLE_W_MAX) warnings.push({ kind: 'widthMax', max: SINGLE_W_MAX });
  if (input.H > SINGLE_H_MAX) warnings.push({ kind: 'heightMax', max: SINGLE_H_MAX });
  const W = Math.max(GEO_MIN_W, input.W - 2 * SINGLE_GAP), H = Math.max(GEO_MIN_H, input.H - SINGLE_GAP);
  const v: GlazingLayoutV = {
    W, H, hinge: 'wall', fixed: [], fixedY: [0, 0], door: [27, W - 27], doorY: [20, H - 27],
    // The drawing's hinge: its door half 340–431 mm up (the dome's top), its
    // frame half down to 246, split at 338 — 180 mm of knuckle.
    axis: [W - 38.5, 30.1], handle: [122, 152], hingeY: [338, H - 340], hingeH: 180,
    hingeArm: PT9_HINGE_ARM, hingeBracket: PT9_HINGE_BRACKET,
    capX: [0, 0], uprof: [], hingeSeal: [0, 0], grow: [],
    profile: 'A', soglia: false, wood: false, noStoppers: true,
  };
  return { input, v, mirror: input.hinge === 'sx', corner: null, frameW: W, frameH: H, warnings };
}

/** Everything the door is made of, in its frame, the leaf turned `open` degrees
 *  outward on its hinges. */
export function singleDoorSolids(p: GlazingPlan, open = 0): GlazingSolid[] {
  const v = p.v, W = v.W, H = v.H;
  const out: GlazingSolid[] = [];
  const part = { kind: 'frame' } as const;
  const put = (stuff: GlazingSolid['stuff'], flats: number[][], at: (x: number, z: number, end: 0 | 1) => P3): void => {
    const sec = orient(loopsOf(flats));
    out.push({ stuff, part, sec, ends: [sec.map((l) => l.map((q) => at(q.x, q.y, 0))), sec.map((l) => l.map((q) => at(q.x, q.y, 1)))] });
  };
  const members: Array<[GlazingSolid['stuff'], number[][]]> = [['profile', PT9_PROFILE], ['gasket', PT9_CLIP], ['seal', PT9_LIP]];
  for (const [stuff, flats] of members) {
    // The jambs from the floor to the head's mitre, and the head between them.
    put(stuff, flats, (x, z, e) => ({ x, y: e ? H - x : 0, z }));
    put(stuff, flats, (x, z, e) => ({ x: W - x, y: e ? H - x : 0, z }));
    put(stuff, flats, (x, z, e) => ({ x: e ? W - x : x, y: H - x, z }));
  }
  out.push(...leafSolids(v, p.input, open));
  return p.mirror ? out.map((s) => mirrorSolid(s, W)) : out;
}
